import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { useSlider } from '../../SliderContext';
import type { ElementSize } from '../utils';
import { useElementSize } from '../utils';

interface NavigationElementContainerProps
  extends PropsWithChildren,
    HTMLAttributes<HTMLDivElement> {
  elementClass: string;
  className?: string;
}

export const NavigationElementContainer = forwardRef<
  HTMLDivElement,
  NavigationElementContainerProps
>(function ({ children, elementClass, className, ...forwardingProps }, ref) {
  const { appClassnamePrefix } = useSlider();

  return (
    <div
      className={classNames(className, `${appClassnamePrefix}-${elementClass}`)}
      {...forwardingProps}
      ref={ref}
    >
      {children}
    </div>
  );
});

interface StyledLayoutContainerProps extends NavigationElementContainerProps {
  $_position: string;
}

const StyledLayoutContainer = styled(
  NavigationElementContainer
)<StyledLayoutContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;

  width: 100%;

  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  z-index: 1;

  pointer-events: none;

  ${({ $_position }) => {
    const isBottom = $_position.startsWith('bottom');
    const isBottomCenter = $_position === 'bottomCenter';
    return css`
      ${isBottom ? 'height: auto;' : 'height: 100%; align-items: flex-end;'}
      ${isBottom && !isBottomCenter && 'justify-content: space-between;'}
    `;
  }}
`;

interface LayoutContainerProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithChildren {
  position: string;
  containerWidth?: number;
  containerScrollWidth?: number;
}

export const LayoutContainer = forwardRef<HTMLDivElement, LayoutContainerProps>(
  function (
    {
      children,
      position,
      containerWidth,
      containerScrollWidth,
      ...forwardingProps
    },
    ref
  ) {
    const shouldCenter =
      containerWidth &&
      containerScrollWidth &&
      containerScrollWidth > containerWidth;

    const centeringStyle = shouldCenter
      ? {
          marginLeft: `-${(containerScrollWidth - containerWidth) / 2}px`
        }
      : {};

    return (
      <StyledLayoutContainer
        elementClass="navigation-layout-container"
        style={centeringStyle}
        $_position={position}
        {...forwardingProps}
        ref={ref}
      >
        {children}
      </StyledLayoutContainer>
    );
  }
);

interface StyledArrowsContainerProps extends NavigationElementContainerProps {
  $_position: string;
  $_isNarrow?: boolean;
}

const StyledArrowsContainer = styled(
  NavigationElementContainer
)<StyledArrowsContainerProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  height: fit-content;
  padding: 16px;
  gap: 8px;

  pointer-events: none;

  ${({ $_isNarrow, $_position }) => {
    const isBottom = $_position.startsWith('bottom');
    const isTop = $_position.startsWith('top');
    const isCenter = $_position === 'center';
    const isLeft = $_position.endsWith('Left');

    return css`
      ${$_isNarrow && 'padding-left: 0; padding-right: 0;'}
      ${isTop && 'top: 0;'}
      ${isTop && (isLeft ? 'left: 0;' : 'right: 0;')}
      ${isCenter &&
      'width: 100%; top: 50%; transform: translateY(-50%); justify-content: space-between;'}
      position: ${isBottom ? 'static' : 'absolute'};
    `;
  }}
`;

interface ArrowsContainerProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithChildren {
  position: string;
  isNarrow?: boolean;
}

export const ArrowsContainer = forwardRef<HTMLDivElement, ArrowsContainerProps>(
  function ({ children, position, isNarrow, ...forwardingProps }, ref) {
    return (
      <StyledArrowsContainer
        elementClass="arrows-container"
        $_isNarrow={isNarrow}
        $_position={position}
        {...forwardingProps}
        ref={ref}
      >
        {children}
      </StyledArrowsContainer>
    );
  }
);

interface StyledPaginationContainerProps
  extends NavigationElementContainerProps {}

const StyledPaginationContainer = styled(
  NavigationElementContainer
)<StyledPaginationContainerProps>`
  pointer-events: none;
  position: static;
`;

const positionToCenteringMarginType = {
  bottomRight: 'marginLeft',
  bottomLeft: 'marginRight'
};

function couldPositionBeCentered(
  value: string
): value is keyof typeof positionToCenteringMarginType {
  return value in positionToCenteringMarginType;
}

interface PaginationContainerProps extends PropsWithChildren {
  position: string;
  shouldCenter?: boolean;
  onSizeChange?: (newSize: ElementSize) => void;
}

export function PaginationContainer({
  children,
  position,
  shouldCenter = false,
  onSizeChange = () => {}
}: PaginationContainerProps) {
  const [containerRef, { width: containerWidth }] =
    useElementSize(onSizeChange);

  const centeringMarginType =
    couldPositionBeCentered(position) &&
    positionToCenteringMarginType[position];
  const centeringMarginValue = `calc(50% - ${containerWidth / 2}px)`;
  const centeringStyle =
    shouldCenter && centeringMarginType
      ? { [centeringMarginType]: centeringMarginValue }
      : {};

  return (
    <StyledPaginationContainer
      elementClass="pagination-container"
      style={centeringStyle}
      ref={containerRef}
    >
      {children}
    </StyledPaginationContainer>
  );
}
